import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { ref, onValue, query, orderByChild, equalTo } from "firebase/database";
import { FiArrowLeft, FiClock, FiPlusCircle, FiCheckCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // i18n hook import kiya

const RechargeHistory = () => {
    const { t } = useTranslation(); // Translation function initialize kiya
    const [activeTab, setActiveTab] = useState('pending');
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const savedUser = localStorage.getItem('user');
        if (!savedUser) {
            navigate('/login');
            return;
        }
        const currentUser = JSON.parse(savedUser);

        // ✅ Query optimized: Ab query server-side par hi sirf current user ka data filter karegi
        const pendingRef = query(
            ref(db, 'depositRequests'), 
            orderByChild('username'), 
            equalTo(currentUser.username)
        );
        
        const approvedRef = query(
            ref(db, 'depositHistory'), 
            orderByChild('username'), 
            equalTo(currentUser.username)
        );
        
        let pendingData = [];
        let approvedData = [];

        // Pending data fetch (Sirf apne username ka)
        const unsubPending = onValue(pendingRef, (snapshot) => {
            const data = snapshot.val();
            pendingData = [];
            if (data) {
                Object.keys(data).forEach(key => {
                    pendingData.push({ id: key, ...data[key], type: 'pending' });
                });
            }
            combineAndSet();
        });

        // Approved data fetch (Sirf apne username ka)
        const unsubApproved = onValue(approvedRef, (snapshot) => {
            const data = snapshot.val();
            approvedData = [];
            if (data) {
                Object.keys(data).forEach(key => {
                    approvedData.push({ id: key, ...data[key], type: 'approved' });
                });
            }
            combineAndSet();
        });

        const combineAndSet = () => {
            const combined = [...pendingData, ...approvedData];
            // Sort by timestamp (newest first)
            setHistory(combined.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)));
            setLoading(false);
        };

        return () => {
            unsubPending();
            unsubApproved();
        };
    }, [navigate]);

    // Tab filter logic
    const filteredData = history.filter(item => {
        if (activeTab === 'pending') {
            return item.status?.toLowerCase() === 'pending';
        } else {
            return item.status?.toLowerCase() === 'success' || item.status?.toLowerCase() === 'approved';
        }
    });

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <FiArrowLeft onClick={() => navigate(-1)} size={22} style={{cursor: 'pointer'}} />
                <h2 style={styles.title}>{t('recharge_history')}</h2>
                <div style={{width: 22}}></div>
            </div>

            <div style={styles.tabContainer}>
                <div 
                    onClick={() => setActiveTab('pending')}
                    style={{...styles.tab, borderBottom: activeTab === 'pending' ? '3px solid #f3ba2f' : 'none', color: activeTab === 'pending' ? '#f3ba2f' : '#888'}}
                >
                    {t('pending')}
                </div>
                <div 
                    onClick={() => setActiveTab('approved')}
                    style={{...styles.tab, borderBottom: activeTab === 'approved' ? '3px solid #f3ba2f' : 'none', color: activeTab === 'approved' ? '#f3ba2f' : '#888'}}
                >
                    {t('approved')}
                </div>
            </div>

            <div style={styles.listContainer}>
                {loading ? (
                    <p style={styles.emptyMsg}>{t('loading_records')}</p>
                ) : filteredData.length > 0 ? (
                    filteredData.map((item) => (
                        <div key={item.id} style={styles.card}>
                            <div style={styles.cardIcon}>
                                {activeTab === 'approved' ? 
                                    <FiCheckCircle color="#0ecb81" size={20} /> : 
                                    <FiPlusCircle color="#f3ba2f" size={20} />
                                }
                            </div>
                            <div style={styles.cardInfo}>
                                <div style={styles.row}>
                                    <span style={styles.amount}>+{item.amount} {item.currency || 'USDT'}</span>
                                    <span style={{
                                        ...styles.statusBadge, 
                                        background: activeTab === 'pending' ? '#fff3cd' : '#d1e7dd', 
                                        color: activeTab === 'pending' ? '#856404' : '#0f5132'
                                    }}>
                                        {t(item.status?.toLowerCase()) || item.status?.toUpperCase()}
                                    </span>
                                </div>
                                <div style={styles.row}>
                                    <span style={styles.date}>
                                        {item.processedAt ? new Date(item.processedAt).toLocaleString() : (item.timestamp ? new Date(item.timestamp).toLocaleString() : t('recent'))}
                                    </span>
                                    <span style={styles.txId}>ID: ...{item.id ? item.id.slice(-6) : ''}</span>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div style={styles.noData}>
                        <FiClock size={40} color="#ccc" />
                        <p style={styles.emptyMsg}>
                            {activeTab === 'pending' ? t('no_pending_records') : t('no_approved_records')}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

const styles = {
    container: { background: '#f8f9fa', minHeight: '100vh', padding: '15px', color: '#333' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    title: { fontSize: '18px', fontWeight: 'bold' },
    tabContainer: { display: 'flex', background: '#fff', borderRadius: '10px', marginBottom: '20px', overflow: 'hidden', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' },
    tab: { flex: 1, textAlign: 'center', padding: '12px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: '0.3s' },
    listContainer: { display: 'flex', flexDirection: 'column', gap: '12px' },
    card: { background: '#fff', padding: '15px', borderRadius: '12px', display: 'flex', gap: '15px', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
    cardIcon: { background: '#f8f9fa', padding: '10px', borderRadius: '50%' },
    cardInfo: { flex: 1 },
    row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' },
    amount: { fontWeight: 'bold', fontSize: '16px', color: '#333' },
    date: { fontSize: '11px', color: '#999' },
    txId: { fontSize: '10px', color: '#bbb' },
    statusBadge: { fontSize: '10px', padding: '3px 8px', borderRadius: '4px', fontWeight: 'bold' },
    noData: { textAlign: 'center', marginTop: '50px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' },
    emptyMsg: { color: '#888', fontSize: '14px' }
};

export default RechargeHistory;